import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { rateGroupAPI } from '../services/api';
import type { RateGroup } from '../types';

const RateGroupList: React.FC = () => {
  const navigate = useNavigate();
  const [rateGroups, setRateGroups] = useState<RateGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchRateGroups();
  }, []);

  const fetchRateGroups = async () => {
    try {
      const response = await rateGroupAPI.getAll();
      setRateGroups(response.data.results);
    } catch (error) {
      console.error('Failed to fetch rate groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this rate group?')) {
      try {
        await rateGroupAPI.delete(id);
        fetchRateGroups();
      } catch (error) {
        console.error('Failed to delete rate group:', error);
        alert('Failed to delete rate group');
      }
    }
  };

  const toggleExpanded = (id: number) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  if (loading) return <div className="container">Loading...</div>;

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2>Rate Groups</h2>
          <button
            onClick={() => navigate('/rate-groups/new')}
            className="btn btn-primary"
          >
            Create New Rate Group
          </button>
        </div>

        {rateGroups.length === 0 ? (
          <p>No rate groups found. Create one to get started.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: '30px' }}></th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Rates Count</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rateGroups.map((group) => (
                  <React.Fragment key={group.id}>
                    <tr>
                      <td>
                        <button
                          onClick={() => toggleExpanded(group.id!)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1.2rem',
                            padding: '0.25rem',
                          }}
                        >
                          {expandedGroups.has(group.id!) ? '▼' : '▶'}
                        </button>
                      </td>
                      <td>
                        <strong>{group.name}</strong>
                      </td>
                      <td style={{ maxWidth: '300px' }}>
                        {group.description || '-'}
                      </td>
                      <td>
                        <span style={{
                          backgroundColor: '#ecf6fc',
                          color: '#3498db',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontWeight: 'bold',
                        }}>
                          {group.rates_count || 0}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          backgroundColor: group.is_active ? '#d4edda' : '#f8d7da',
                          color: group.is_active ? '#155724' : '#721c24',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                        }}>
                          {group.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        {group.created_at ? new Date(group.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="actions">
                        <button
                          onClick={() => navigate(`/rate-groups/${group.id}/edit`)}
                          className="btn btn-small"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(group.id!)}
                          className="btn btn-danger btn-small"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                    {expandedGroups.has(group.id!) && (
                      <tr>
                        <td colSpan={7} style={{ padding: '1rem', backgroundColor: '#f8f9fa' }}>
                          <div style={{ paddingLeft: '2rem' }}>
                            <h4 style={{ marginBottom: '0.75rem' }}>Billing Rates in this Group:</h4>
                            {group.rate_group_memberships && group.rate_group_memberships.length > 0 ? (
                              <div style={{ display: 'grid', gap: '0.5rem' }}>
                                {group.rate_group_memberships.map((membership) => (
                                  <div 
                                    key={membership.id}
                                    style={{
                                      backgroundColor: 'white',
                                      padding: '0.75rem',
                                      borderRadius: '4px',
                                      border: '1px solid #ddd',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <div>
                                      <strong>{membership.project_code} - {membership.billing_rate_name}</strong>
                                      <div style={{ fontSize: '0.85rem', color: '#7f8c8d' }}>
                                        {membership.project_name}
                                      </div>
                                    </div>
                                    <div style={{ 
                                      fontWeight: 'bold',
                                      color: '#2c3e50',
                                    }}>
                                      ${membership.billing_rate_details?.rate_amount}/hr
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p style={{ color: '#7f8c8d', margin: 0 }}>No rates in this group yet.</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RateGroupList;

